import React, { useState } from 'react';

const DEC_COLOR = { New: '#1A7F37', Refactor: '#9A6700', Retire: '#CF222E' };
const DEC_BG    = { New: '#DAFBE1', Refactor: '#FFF8C5', Retire: '#FFEBE9' };

export default function RequirementsGate({ data, onApprove }) {
  const reqs = data?.requirements || [];

  // edited: the full requirements text per app (starts as LLM draft)
  const [edited, setEdited] = useState(() =>
    Object.fromEntries(reqs.map(r => [r.app_name, r.llm_draft || '']))
  );
  // extensions: additional notes/requirements the user types
  const [extensions, setExtensions] = useState(() =>
    Object.fromEntries(reqs.map(r => [r.app_name, '']))
  );
  const [activeTab, setActiveTab]   = useState(reqs[0]?.app_name || '');
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    // Merge extension into the approved text if user added anything
    const requirements = reqs.map(r => {
      const base = edited[r.app_name] || r.llm_draft || '';
      const ext  = extensions[r.app_name]?.trim();
      const approved_text = ext
        ? `${base}\n\n## Additional Requirements (User Added)\n${ext}`
        : base;
      return { app_name: r.app_name, approved_text, notes: ext || '' };
    });
    await onApprove(requirements);
  };

  const active     = reqs.find(r => r.app_name === activeTab);
  const wordCount  = (edited[activeTab] || '').split(/\s+/).filter(Boolean).length;
  const extCount   = (extensions[activeTab] || '').trim().length;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 1100, maxHeight: '94vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        animation: 'modal-in 0.2s ease',
      }}>
        <style>{`@keyframes modal-in { from { opacity:0; transform:translateY(-12px) scale(0.98); } to { opacity:1; transform:none; } }`}</style>

        {/* ── Header ── */}
        <div style={{ padding: '18px 26px 14px', background: '#DAFBE1', borderBottom: '1px solid #D0D7DE', borderRadius: '14px 14px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>📝</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#1A7F37' }}>
                Your Input Needed — Requirements Review
              </div>
              <div style={{ fontSize: 12, color: '#57606A', marginTop: 2 }}>
                AI-generated requirements for each application are ready. Edit the text directly or add your own requirements below. Approve when ready to start building.
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: 'flex', borderBottom: '1px solid #D0D7DE', background: '#F6F8FA', overflowX: 'auto', flexShrink: 0 }}>
          {reqs.map(r => {
            const isActive = activeTab === r.app_name;
            const color    = DEC_COLOR[r.decision] || '#57606A';
            return (
              <button key={r.app_name} onClick={() => setActiveTab(r.app_name)} style={{
                padding: '10px 18px', border: 'none', cursor: 'pointer',
                borderBottom: isActive ? `3px solid ${color}` : '3px solid transparent',
                background: isActive ? '#fff' : 'transparent',
                color: isActive ? color : '#57606A',
                fontWeight: isActive ? 700 : 400, fontSize: 12,
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
              }}>
                {r.app_name.replace('legacy-', '')}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                  background: DEC_BG[r.decision] || '#F6F8FA',
                  color: DEC_COLOR[r.decision] || '#57606A',
                }}>
                  {r.decision}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Editor body ── */}
        {active && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Section 1: LLM-generated requirements (editable) */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1F2E' }}>
                  📋 AI-Generated Requirements
                  <span style={{ fontWeight: 400, color: '#57606A', marginLeft: 8, fontSize: 11 }}>
                    — edit directly
                  </span>
                </div>
                <span style={{ fontSize: 11, color: '#57606A' }}>{wordCount} words</span>
              </div>
              <textarea
                value={edited[active.app_name] || ''}
                onChange={e => setEdited(prev => ({ ...prev, [active.app_name]: e.target.value }))}
                disabled={submitting}
                rows={14}
                style={{
                  width: '100%', background: '#F6F8FA',
                  border: '1px solid #D0D7DE', borderRadius: 8,
                  color: '#1A1F2E', fontFamily: "'Fira Code', monospace",
                  fontSize: 12, padding: 14, resize: 'vertical',
                  outline: 'none', lineHeight: 1.7,
                }}
                onFocus={e => e.target.style.borderColor = '#0969DA'}
                onBlur={e  => e.target.style.borderColor = '#D0D7DE'}
              />
            </div>

            {/* Section 2: User extensions */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1A1F2E' }}>
                  ✏️ Your Additional Requirements
                  <span style={{ fontWeight: 400, color: '#57606A', marginLeft: 8, fontSize: 11 }}>
                    — appended to the requirements doc
                  </span>
                </div>
                {extCount > 0 && (
                  <span style={{ fontSize: 11, color: '#0969DA', fontWeight: 600 }}>
                    {extCount} chars added
                  </span>
                )}
              </div>
              <textarea
                value={extensions[active.app_name] || ''}
                onChange={e => setExtensions(prev => ({ ...prev, [active.app_name]: e.target.value }))}
                disabled={submitting}
                rows={5}
                placeholder={`Add extra requirements for ${active.app_name.replace('legacy-','')}...\n\nExamples:\n- Add WebSocket support for real-time updates\n- Must support multi-tenancy\n- Rate limit all endpoints to 100 req/min`}
                style={{
                  width: '100%', background: '#fff',
                  border: '2px dashed #D0D7DE', borderRadius: 8,
                  color: '#1A1F2E', fontFamily: "'Fira Code', monospace",
                  fontSize: 12, padding: 14, resize: 'vertical',
                  outline: 'none', lineHeight: 1.7,
                }}
                onFocus={e => e.target.style.borderColor = '#0969DA'}
                onBlur={e  => e.target.style.borderColor = '#D0D7DE'}
              />
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div style={{
          padding: '14px 26px', borderTop: '1px solid #D0D7DE',
          background: '#F6F8FA', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          borderRadius: '0 0 14px 14px', flexWrap: 'wrap', gap: 10,
        }}>
          <div style={{ fontSize: 12, color: '#57606A' }}>
            {reqs.length} app{reqs.length !== 1 ? 's' : ''} ·{' '}
            {reqs.filter(r => r.decision === 'New').length} new builds ·{' '}
            {reqs.filter(r => r.decision === 'Refactor').length} refactors ·{' '}
            {reqs.filter(r => extensions[r.app_name]?.trim()).length} with extra requirements
          </div>
          {submitting ? (
            <span style={{ color: '#57606A', fontSize: 13 }}>
              <span className="spin" style={{ display: 'inline-block', marginRight: 6 }}>⟳</span>
              Starting build…
            </span>
          ) : (
            <button
              onClick={handleApprove}
              style={{
                background: '#1A7F37', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 20px', fontSize: 13,
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              ✅ Approve All &amp; Start Coding
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
